import { useEffect, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { modoTeclado, origenDe, TEXTO_ORIGEN, validarEnVivo, valorDe } from './fichaAtributos';

/* =====================================================================
   EL BLOQUE DE ATRIBUTOS QUE SE ADAPTA · CATÁLOGO VIVO F2
   =====================================================================
   Pinta los datos que pide la categoría (con los heredados de sus madres),
   cada uno según su tipo, y valida mientras se escribe. Lo usan la ficha de
   edición de este carril y, por parche, las altas de otros carriles
   (Productos, Ficha previa de alta por escaneo).

   Uso:
     <BloqueAtributos idCategoria={cat} memoria={mem} onCambio={setMem}
       atributosGuardados={lista} origenes={{ marca: 'de Open Library' }} />
   `memoria` es el mapa de lo tecleado por clave (ver fichaAtributos.js);
   el bloque no lo guarda: quien lo usa decide cuándo mandar `paraGuardar`.
   `onPlantilla` recibe la plantilla cada vez que cambia la categoría.
   `sinIdentidad`: no pinta los datos que viven en la identidad del producto
   (marca, autores, año… de la 1142), para la ficha que ya los pide a mano.
   ===================================================================== */
const CAMPO = 'w-full min-h-12 rounded-xl border px-3 text-base bg-white';

function Campo({ atributo, valor, onValor, origen }) {
  const id = `cv-atributo-${atributo.clave}`;
  const [tocado, setTocado] = useState(false);
  const juicio = validarEnVivo(atributo, valor);
  const mostrarError = !juicio.valido && (tocado || !juicio.faltaObligatorio);
  const borde = mostrarError ? 'border-red-400' : 'border-gray-300';
  const comun = { id, 'aria-invalid': mostrarError || undefined, 'aria-describedby': `${id}-nota`,
    onBlur: () => setTocado(true) };
  let control;
  switch (atributo.tipo) {
    case 'BOOLEANO': {
      const si = ['si', 'sí', 'true', '1'].includes(String(valor).toLowerCase()) || valor === true;
      control = (
        <label className="inline-flex items-center gap-3 min-h-12">
          <input type="checkbox" role="switch" className="h-6 w-6" checked={si} {...comun}
            onChange={(e) => onValor(e.target.checked ? 'sí' : 'no')} />
          <span className="text-sm text-gray-800">{si ? 'Sí' : 'No'}</span>
        </label>
      );
      break;
    }
    case 'LISTA':
      control = (
        <select className={`${CAMPO} ${borde}`} value={valor ?? ''} {...comun} onChange={(e) => onValor(e.target.value)}>
          <option value="">Elige…</option>
          {(atributo.opciones || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
      break;
    case 'LISTA_MULTIPLE': {
      const elegidas = Array.isArray(valor) ? valor : String(valor || '').split(',').map((x) => x.trim()).filter(Boolean);
      control = (
        <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`${id}-etiqueta`}>
          {(atributo.opciones || []).map((o) => {
            const activa = elegidas.includes(o);
            return (
              <button key={o} type="button" aria-pressed={activa}
                onClick={() => onValor(activa ? elegidas.filter((x) => x !== o) : [...elegidas, o])}
                className={`min-h-11 rounded-full border px-3 text-sm ${activa ? 'border-[var(--color-marca)] bg-gray-100 font-semibold' : 'border-gray-300'}`}>
                {o}
              </button>
            );
          })}
        </div>
      );
      break;
    }
    case 'FECHA':
      control = <input type="date" className={`${CAMPO} ${borde}`} value={valor ?? ''} {...comun} onChange={(e) => onValor(e.target.value)} />;
      break;
    default:
      control = (
        <div className="flex items-center gap-2">
          <input type="text" inputMode={modoTeclado(atributo)} autoComplete="off" className={`${CAMPO} ${borde}`}
            value={valor ?? ''} {...comun} onChange={(e) => onValor(e.target.value)}
            placeholder={atributo.tipo === 'NUMERO' && atributo.unidad_base === 'Ω' ? '10k, 4k7, 220' : ''} />
          {atributo.unidad_base && <span className="text-sm text-gray-700 shrink-0">{atributo.unidad_base}</span>}
        </div>
      );
  }
  return (
    <div data-atributo={atributo.clave} className="space-y-1">
      <label htmlFor={id} id={`${id}-etiqueta`} className="block text-sm font-semibold text-gray-700">
        {atributo.nombre}{atributo.obligatorio && <span className="text-red-700"> *</span>}
      </label>
      {control}
      <p id={`${id}-nota`} className="text-xs min-h-4">
        {mostrarError && <span className="text-red-700">{juicio.mensaje}</span>}
        {!mostrarError && juicio.muestra && <span className="text-green-800">Se guarda como {juicio.muestra}</span>}
        {!mostrarError && !juicio.muestra && origen && <span className="text-gray-500">{TEXTO_ORIGEN[origen] || origen}</span>}
      </p>
      {atributo.ayuda && (
        <p className="text-xs text-gray-600 inline-flex items-start gap-1">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />{atributo.ayuda}
        </p>
      )}
    </div>
  );
}

export default function BloqueAtributos({ idCategoria, memoria, onCambio, atributosGuardados, origenes, onPlantilla,
  sinIdentidad = false }) {
  // La plantilla se recuerda con la categoría y lo guardado de los que salió:
  // si cambian, la de antes deja de valer sola, sin limpiar nada a mano.
  const [leida, setLeida] = useState({ para: null, lista: null, error: null });
  const vigente = leida.para && leida.para.id === idCategoria && leida.para.guardados === atributosGuardados;
  const plantilla = vigente ? leida.lista : null;
  const error = vigente ? leida.error : null;

  useEffect(() => {
    if (idCategoria == null) { onPlantilla?.([]); return undefined; }
    let vivo = true;
    const para = { id: idCategoria, guardados: atributosGuardados };
    api.get(`/catalogo-vivo/catalogo/categorias/${idCategoria}/plantilla`)
      .then((r) => {
        if (!vivo) return;
        // Lo guardado en el producto pone el valor de partida de cada dato.
        const guardado = Object.fromEntries((atributosGuardados || []).map((a) => [a.clave, a.valor]));
        const lista = (r.atributos || []).filter((a) => !(sinIdentidad && a.almacen))
          .map((a) => ({ ...a, valor: guardado[a.clave] ?? null }));
        setLeida({ para, lista, error: null });
        onPlantilla?.(lista);
      })
      .catch((e) => {
        if (vivo) setLeida({ para, lista: null, error: e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.' });
      });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCategoria, atributosGuardados, sinIdentidad]);

  if (idCategoria == null) return null;
  if (error) return <p role="alert" className="text-sm text-red-700">{error}</p>;
  if (plantilla == null) {
    return <p className="text-sm text-gray-600 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo los datos de la categoría…</p>;
  }
  if (plantilla.length === 0) return null;
  return (
    <fieldset className="space-y-3" data-catalogo-vivo="bloque-atributos">
      <legend className="text-sm font-semibold text-gray-800">Datos de la categoría</legend>
      <Ayuda id="catalogo_vivo.ficha.atributos" />
      {plantilla.map((a) => (
        <Campo key={a.clave} atributo={a} valor={valorDe(a, memoria)} origen={origenDe(a, memoria, origenes)}
          onValor={(v) => onCambio({ ...memoria, [a.clave]: v })} />
      ))}
    </fieldset>
  );
}
